import { CLIENT_LICENSE_LIST_FIELDS } from '@supply-mover-2-0/shared';
import type { ListBuilderConfiguration } from '../../types';
import { activeCustomersList } from './lists/active-customers-list';
import { atRiskList } from './lists/at-risk-list';
import { contactDueList } from './lists/contact-due-list';
import { defaultClientsAllList } from './lists/default-clients-all';
import { defaultClientsNearMeList } from './lists/default-clients-near-me';
import { defaultClientsOpenOrdersList } from './lists/default-clients-open-orders';
import { dormantCustomersList } from './lists/dormant-customers-list';
import { myClientsList } from './lists/my-clients-list';
import { newLeadsList } from './lists/new-leads-list';
import { overdueList } from './lists/overdue-list';
import { prospectingList } from './lists/prospecting-list';
import { clientRoutingList } from './lists/routing-list';
import { clientSalesRoutingList } from './lists/sales-routing-list';
import { scheduledTodayList } from './lists/scheduled-today-list';
import { suggestedSalesList } from './lists/suggested-sales-list';
import { unassignedClientsList } from './lists/unassigned-clients-list';

/**
 * Client list configuration
 * Includes multiple filtered views for managing clients and leads
 */
export const CLIENT_CONFIG: ListBuilderConfiguration = {
	objectType: 'client',
	// PD-12353: opts the client list into the record-import framework. The
	// importable-field catalog lives server-side (ClientRecordImporter); the
	// field arrays here are unused for clients and kept empty on purpose.
	importExport: {
		allowExport: true,
		allowImport: true,
		exportFields: [],
		importFields: [],
	},
	lists: [
		defaultClientsAllList,
		scheduledTodayList,
		overdueList,
		contactDueList,
		suggestedSalesList,
		atRiskList,
		defaultClientsOpenOrdersList,
		defaultClientsNearMeList,
		myClientsList,
		newLeadsList,
		prospectingList,
		activeCustomersList,
		dormantCustomersList,
		unassignedClientsList,
		clientRoutingList,
		clientSalesRoutingList,
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50],
	},
	showResetButton: true,
	filters: {
		activeFilters: [
			'clientSearch',
			'clientCompany',
			'salespeople',
			'alternateRep',
			'lifecycleStageName',
			'businessTypes',
			'leadOrCustomer',
			'salesStatus',
			'callStatus',
			// PD-15504 — 'which customers have no route?' is the question the
			// Routing list exists for, so the chip rides on every clients list
			// rather than only that one.
			'clientRouted',
			// PD-15698 — "who haven't we visited lately?" pairs with the
			// Routing list's last-stop column but is useful on any client list.
			'lastStopDays',
			// PD-15918 — sales-visit and delivery recency over the denormalised
			// client.lastVisitDate / lastDeliveryDate columns.
			'lastVisitDays',
			'lastDeliveryDays',
			// PD-15407: the simple State multi-select is the default location
			// filter; the combined geography search and the Near/Radius proximity
			// filter stay registered (listKeys) so users can re-add them via the
			// List Configuration modal, and the bundled Near Me tab keeps its own
			// override with nearMe active.
			'clientState',
			'revenue90Days',
			'orderSalesStatus',
			'groups',
			'clientLists',
			'excludeClientLists',
			'clientDeletedRecords',
			'clientArchivedRecords',
		],
		// PD-14228: the Ownership multi-select filter is intentionally NOT listed
		// here, so it is OFF by default. Its `listKeys: ['client']` registration
		// puts it in the List Configuration modal's available pool, where users
		// can drag it in when they want it.
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/clients/:id',
			objectLabel: 'Client',
			objectTable: 'client',
			// Rows on this list are client records keyed by `id`, so any
			// configured column of the table can be filled in server-side
			// (ListFieldHydrationService).
			rowIdField: 'id',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'legalName', fieldLabel: 'Legal Name' },
				{ fieldName: 'email', fieldLabel: 'Email' },
				{ fieldName: 'phone', fieldLabel: 'Phone' },
				{ fieldName: 'address1', fieldLabel: 'Address 1' },
				{ fieldName: 'city', fieldLabel: 'City' },
				{ fieldName: 'state', fieldLabel: 'State' },
				{ fieldName: 'zip', fieldLabel: 'Zip' },
				{ fieldName: 'lastCallDate', fieldLabel: 'Last Call Date' },
				{ fieldName: 'lastContactDate', fieldLabel: 'Last Contact Date' },
				// PD-15918 — denormalised sales-visit / delivery recency.
				{ fieldName: 'lastVisitDate', fieldLabel: 'Last Visit Date' },
				{ fieldName: 'lastDeliveryDate', fieldLabel: 'Last Delivery Date' },
				// PD-15924 — the next planned sales stop (computed when requested).
				{
					fieldName: 'nextSalesStopDate',
					fieldLabel: 'Next Planned Sales Stop',
				},
				{ fieldName: 'lastNoteText', fieldLabel: 'Last Note' },
				{ fieldName: 'salesStatus', fieldLabel: 'erpStatus' },
				{ fieldName: 'leadOrCustomer', fieldLabel: 'Lead / Customer' },
				{ fieldName: 'creditBalance', fieldLabel: 'Credit Balance' },
				{ fieldName: 'creditLimit', fieldLabel: 'Credit Limit' },
				{ fieldName: 'hasCreditHold', fieldLabel: 'Credit Hold' },
				{
					fieldName: 'averageInvoiceRevenue',
					fieldLabel: 'Average Invoice Revenue',
				},
				{ fieldName: 'invoiceFrequency', fieldLabel: 'Invoice Frequency' },
				{
					fieldName: 'forecastedReorderDate',
					fieldLabel: 'Forecasted Reorder Date',
				},
				// PD-13536: expose the rolling revenue trend ratio pair (the same
				// fields the item and shop pickers offer, and the pair SM1's client
				// list exposes) in place of the legacy `revenueTrend90` regression
				// slope column. Saved layouts still referencing the old field keep
				// rendering via the humanized-label fallback.
				{
					fieldName: 'rollingRevenueTrend30',
					fieldLabel: 'Revenue 30 Day Trend (%)',
				},
				{
					fieldName: 'rollingRevenueTrend90',
					fieldLabel: 'Revenue 90 Day Trend (%)',
				},
				{ fieldName: '_created_date', fieldLabel: 'Created Date' },
				{ fieldName: 'latitude', fieldLabel: 'Latitude' },
				{ fieldName: 'longitude', fieldLabel: 'Longitude' },
				{ fieldName: 'googlePlaceId', fieldLabel: 'Google Place ID' },
				// PD-14202: additional pickable client list fields.
				// `lastInvoiceDate` / `ncpdpId` are real client columns (pass straight
				// through the backend field resolver). `companyNames` is the backend's
				// comma-joined company list (resolved via getCompanyNamesForEntities).
				// `lastNoteDate` / `lastInvoiceNumber` are computed aliases the clients
				// service joins in on demand (see clients.service.ts field resolution).
				{ fieldName: 'companyNames', fieldLabel: 'Company' },
				{ fieldName: 'lastInvoiceDate', fieldLabel: 'Last Invoice Date' },
				{ fieldName: 'lastInvoiceNumber', fieldLabel: 'Last Invoice #' },
				{ fieldName: 'ncpdpId', fieldLabel: 'NCPDP ID' },
				{ fieldName: 'lastNoteDate', fieldLabel: 'Last Note Date' },
				// PD-16077 — license number + expiration date per license type
				// ("DEA License", "DEA Expiration Date", "State Board License",
				// …). Generated from the canonical license-type list, so a new
				// type is offered here automatically. Each is a computed alias
				// the clients service resolves from client_license on demand;
				// none is a client column.
				...CLIENT_LICENSE_LIST_FIELDS.map((licenseField) => ({
					fieldName: licenseField.fieldName,
					fieldLabel: licenseField.fieldLabel,
				})),
			],
		},
	],
	tableSorts: [
		{
			id: 'name',
			name: 'Name',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'name',
					objectTable: 'client',
				},
			],
			description: 'Sort clients by name',
		},
		{
			id: 'recent-contact',
			name: 'Last Contact',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'lastCallDate',
					objectTable: 'client',
				},
			],
			description: 'Sort clients by last contact date',
		},
		{
			id: 'location',
			name: 'By Location',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'state',
					objectTable: 'client',
				},
				{
					direction: 'ASC',
					fieldName: 'city',
					objectTable: 'client',
				},
			],
			description: 'Sort by state then city',
		},
		{
			id: 'next-activity',
			name: 'Next Activity',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'nextActivityTime',
					objectTable: 'task_data',
				},
			],
			description: 'Sort by next activity time',
		},
		{
			id: 'forecast',
			name: 'Forecast',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'forecastedReorderDate',
					objectTable: 'client',
					nullsLast: true,
				},
			],
			description: 'Sort by forecasted reorder date',
		},
		{
			id: 'last-assigned-date-desc',
			name: 'Last Assigned Date (Newest First)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'lastAssignedDate',
					objectTable: 'client',
				},
			],
			description: 'Sort by last assigned date (newest first)',
		},
		{
			id: 'last-assigned-date-asc',
			name: 'Last Assigned Date (Oldest First)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'lastAssignedDate',
					objectTable: 'client',
				},
			],
			description: 'Sort by last assigned date (oldest first)',
		},
		{
			id: 'last-call-date-desc',
			name: 'Last Call Date (Newest First)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'lastCallDate',
					objectTable: 'client',
				},
			],
			description: 'Sort by last call date (newest first)',
		},
		{
			id: 'last-call-date-asc',
			name: 'Last Call Date (Oldest First)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'lastCallDate',
					objectTable: 'client',
				},
			],
			description: 'Sort by last call date (oldest first)',
		},
		{
			id: 'last-invoice-date-desc',
			name: 'Last Invoice Date (Newest First)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'lastInvoiceDate',
					objectTable: 'client',
				},
			],
			description: 'Sort by last invoice date (newest first)',
		},
		{
			id: 'last-invoice-date-asc',
			name: 'Last Invoice Date (Oldest First)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'lastInvoiceDate',
					objectTable: 'client',
				},
			],
			description: 'Sort by last invoice date (oldest first)',
		},
		{
			id: 'rolling-revenue-30-desc',
			name: 'Revenue 30 Days (High to Low)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'rollingRevenue30',
					objectTable: 'client',
				},
			],
			description: 'Sort by 30-day rolling revenue (highest first)',
		},
		{
			id: 'rolling-revenue-30-asc',
			name: 'Revenue 30 Days (Low to High)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'rollingRevenue30',
					objectTable: 'client',
				},
			],
			description: 'Sort by 30-day rolling revenue (lowest first)',
		},
		{
			id: 'gp-90-days-desc',
			name: 'GP 90 Days (High to Low)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'rollingTotalGPAggregate90',
					objectTable: 'client',
				},
			],
			description: 'Sort by gross profit 90 days (highest first)',
		},
		{
			id: 'gp-90-days-asc',
			name: 'GP 90 Days (Low to High)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'rollingTotalGPAggregate90',
					objectTable: 'client',
				},
			],
			description: 'Sort by gross profit 90 days (lowest first)',
		},
		{
			id: 'gp-trend-90-days-desc',
			name: 'GP Trend 90 Days (High to Low)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'rollingTotalGPMargin90',
					objectTable: 'client',
				},
			],
			description: 'Sort by gross profit margin trend 90 days (highest first)',
		},
		{
			id: 'gp-trend-90-days-asc',
			name: 'GP Trend 90 Days (Low to High)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'rollingTotalGPMargin90',
					objectTable: 'client',
				},
			],
			description: 'Sort by gross profit margin trend 90 days (lowest first)',
		},
		{
			id: 'revenue-90-days-desc',
			name: 'Revenue 90 Days (High to Low)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'rollingRevenue90',
					objectTable: 'client',
				},
			],
			description: 'Sort by revenue 90 days (highest first)',
		},
		{
			id: 'revenue-90-days-asc',
			name: 'Revenue 90 Days (Low to High)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'rollingRevenue90',
					objectTable: 'client',
				},
			],
			description: 'Sort by revenue 90 days (lowest first)',
		},
		{
			id: 'revenue-trend-90-days-desc',
			name: 'Revenue Trend 90 Days (High to Low)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'minimumTrendDiff',
					objectTable: 'client',
				},
			],
			description: 'Sort by revenue trend 90 days (highest first)',
		},
		{
			id: 'revenue-trend-90-days-asc',
			name: 'Revenue Trend 90 Days (Low to High)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'minimumTrendDiff',
					objectTable: 'client',
				},
			],
			description: 'Sort by revenue trend 90 days (lowest first)',
		},
	],
};
